import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Badge, Button, Modal, Collapse, Form } from 'react-bootstrap';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { useGetEmailPlatformStatusQuery } from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';

function StatusBadge({ ok }: { ok: boolean | undefined }) {
  if (ok === undefined) return <Badge bg="secondary">Unknown</Badge>;
  return ok ? <Badge bg="success">Up</Badge> : <Badge bg="danger">Down</Badge>;
}

function StorageStatusBadge({ status }: { status: string }) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'danger') return <Badge bg="danger">Danger</Badge>;
  if (normalized === 'warning') return <Badge bg="warning" text="dark">Saturated</Badge>;
  if (normalized === 'ok') return <Badge bg="success">Up</Badge>;
  return <Badge bg="secondary">Unknown</Badge>;
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="d-flex justify-content-between gap-3 kg-kv-row" style={{ fontSize: '0.82rem', lineHeight: 1.15 }}>
      <div className="text-muted" style={{ fontWeight: 600 }}>
        {label}:
      </div>
      <div style={{ fontWeight: 600, textAlign: 'right' }}>{value}</div>
    </div>
  );
}

function KVStack({ children }: { children: React.ReactNode }) {
  return (
    <div className="kg-kv-stack" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
      {children}
    </div>
  );
}

function TileIcon({ size = 112 }: { size?: number }) {
  return (
    <img
      // Use PUBLIC_URL so it works on localhost and when hosted under a sub-path
      src={`${process.env.PUBLIC_URL}/kkservers.png`}
      alt=""
      aria-hidden="true"
      className="kg-tile-icon"
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        opacity: 0.9,
      }}
    />
  );
}

function MonitoringPulse({ color = '#0d6efd' }: { color?: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'relative',
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color,
        display: 'inline-block',
        marginRight: 8,
        flex: '0 0 auto',
      }}
      className="kg-monitor-pulse"
    />
  );
}

function TileHeader({
  title,
  subtitle,
  right,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div
      className="kg-tile-header"
      style={{
        position: 'relative',
        borderBottom: '1px solid #e9ecef',
        background: '#f8f9fa',
        padding: '14px 14px',
        // Card has rounded corners + overflow hidden; keep header corners square to match.
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
    >
      {right ? <div className="d-flex justify-content-end mb-2">{right}</div> : null}

      <div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0 }}>
        <TileIcon size={84} />
        <div style={{ minWidth: 0 }}>
          <div
            className="fw-bold kg-tile-title"
            style={{
              lineHeight: 1.1,
              whiteSpace: 'normal',
              wordBreak: 'keep-all',
              overflowWrap: 'normal',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              className="text-muted kg-tile-subtitle"
              style={{
                fontSize: '0.85rem',
                whiteSpace: 'normal',
                wordBreak: 'keep-all',
                overflowWrap: 'normal',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type ImapBannerInfo = {
  ok: boolean;
  okText?: string;
  capabilities: string[];
  serverText?: string;
  raw: string;
};

function parseImapBanner(rawBanner: any): ImapBannerInfo | null {
  const raw = String(rawBanner || '').trim();
  if (!raw) return null;
  const ok = raw.includes('* OK');
  const okText = ok ? '* OK' : undefined;
  const capsMatch = raw.match(/\[CAPABILITY\s+([^\]]+)\]/i);
  const caps = capsMatch?.[1]
    ? capsMatch[1]
        .trim()
        .split(/\s+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const serverText = raw.replace(/\s+/g, ' ').trim();
  return { ok, okText, capabilities: caps, serverText, raw };
}

function formatPct(value: any): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function formatBytes(value: any): string {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let next = bytes;
  let idx = 0;
  while (next >= 1024 && idx < units.length - 1) {
    next = next / 1024;
    idx += 1;
  }
  const digits = next >= 100 ? 0 : next >= 10 ? 1 : 2;
  return `${next.toFixed(digits)} ${units[idx]}`;
}

function formatDurationMinutes(seconds: any): string {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const minutes = Math.max(1, Math.round(n / 60));
  return `${minutes}m`;
}

function formatAuthCanary(payload: any): { label: string; detail: string } {
  const c = payload?.imap?.authCanary;
  if (!c) return { label: '—', detail: 'not reported' };
  if (c.configured === false) return { label: 'Disabled', detail: c.detail || 'disabled' };
  const last = c.lastSampledAt || '—';
  const remediation = c.lastRemediationStatus ? String(c.lastRemediationStatus) : 'none';
  const remediationLower = String(remediation || '').toLowerCase();
  const failed = remediation !== 'none' && remediationLower !== 'success' && !remediationLower.startsWith('skipped:');
  const label = failed ? 'Needs attention' : 'OK';
  const failureAt = c.lastFailureAt || '—';
  const failureDomain = c.lastFailureDomain || '—';
  const failureErr = c.lastFailureError || '—';
  const cmd = c.lastRepairCommandId || '—';
  return {
    label,
    detail: `last=${last} | remediation=${remediation} | lastFailure=${failureAt} ${failureDomain} ${failureErr} | cmd=${cmd}`,
  };
}

const CAPABILITY_HELP: Record<string, { title: string; desc: string }> = {
  IMAP4rev1: {
    title: 'IMAP4rev1',
    desc: 'The server supports the modern IMAP4rev1 email protocol (standard for syncing mail folders).',
  },
  'SASL-IR': {
    title: 'SASL-IR',
    desc: 'Supports sending the initial authentication response in the same step as starting login, which can reduce round trips.',
  },
  'LOGIN-REFERRALS': {
    title: 'LOGIN-REFERRALS',
    desc: 'Can tell a client to connect to another server for the actual login if needed (used in some larger setups).',
  },
  ID: {
    title: 'ID',
    desc: 'Supports the IMAP ID command so the client can send info like its name/version.',
  },
  ENABLE: {
    title: 'ENABLE',
    desc: 'Lets clients turn on optional protocol extensions after they connect.',
  },
  IDLE: {
    title: 'IDLE',
    desc: 'Supports push-style updates, so the server can notify the client immediately when new mail arrives.',
  },
  'LITERAL+': {
    title: 'LITERAL+',
    desc: 'Allows a more efficient way of sending large blocks of data without extra confirmation steps.',
  },
  'AUTH=PLAIN': {
    title: 'AUTH=PLAIN',
    desc: 'Username/password authentication mechanism (intended to be used over an encrypted connection).',
  },
  'AUTH=LOGIN': {
    title: 'AUTH=LOGIN',
    desc: 'Username/password authentication mechanism (intended to be used over an encrypted connection).',
  },
};

type StatusCardFilter = 'all' | 'inbound' | 'outbound' | 'imap' | 'storage' | 'dns';

const STATUS_CARD_FILTERS: Array<{ id: StatusCardFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'inbound', label: 'Inbound' },
  { id: 'outbound', label: 'Outbound' },
  { id: 'imap', label: 'IMAP' },
  { id: 'storage', label: 'Storage' },
  { id: 'dns', label: 'DNS' },
];

function CapabilityTile({ cap }: { cap: string }) {
  const help = CAPABILITY_HELP[cap];
  const title = help?.title || cap;
  const desc = help?.desc || 'IMAP capability flag supported by the server.';
  return (
    <div className="kg-cap-tile">
      <div className="kg-cap-title">{title}</div>
      <div className="kg-cap-desc">{desc}</div>
    </div>
  );
}

export default function EmailPlatformStatus() {
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [compactCards, setCompactCards] = useState(true);
  const [cardFilter, setCardFilter] = useState<StatusCardFilter>('all');
  const { data, isFetching, isLoading, error, refetch } = useGetEmailPlatformStatusQuery(undefined, {
    pollingInterval: autoRefresh ? 30_000 : 0,
    refetchOnFocus: true,
  });

  useEffect(() => {
    (window as any).__EMAIL_PLATFORM_STATUS_BUILD = 'email-platform-status-20260119-0002';
  }, []);

  const payload: any = (data as any)?.data || data;
  const authCanary = formatAuthCanary(payload);
  const imapBannerInfo = parseImapBanner(payload?.imap?.banner?.detail);
  const imapCaps = imapBannerInfo?.capabilities ?? [];
  const imapCapacity = payload?.imap?.capacity || {};
  const outboundCanary = payload?.outboundCanary || {};
  const outboundCanaryConfigured = outboundCanary?.configured === true;
  const outboundCanaryState = String(outboundCanary?.state || '').toUpperCase();
  const outboundCanaryStatusLabel = !outboundCanaryConfigured
    ? 'Not configured'
    : outboundCanaryState === 'OK'
    ? 'OK'
    : outboundCanaryState === 'ALARM'
    ? 'Alarm'
    : outboundCanaryState === 'INSUFFICIENT_DATA'
    ? 'Warming'
    : outboundCanaryState || 'Unknown';
  const outboundCanaryPulseColor = !outboundCanaryConfigured
    ? '#6c757d'
    : outboundCanaryState === 'OK'
    ? '#198754'
    : outboundCanaryState === 'ALARM'
    ? '#dc3545'
    : '#fd7e14';
  const outboundCanaryBadgeVariant = !outboundCanaryConfigured
    ? 'secondary'
    : outboundCanaryState === 'OK'
    ? 'success'
    : outboundCanaryState === 'ALARM'
    ? 'danger'
    : 'warning';
  const storage = payload?.storage || {};
  const storageStatus = String(storage?.status || 'unknown').toLowerCase();
  const storageStatusNow = String(storage?.statusNow || storageStatus || 'unknown').toLowerCase();
  const storageDanger = storageStatus === 'danger';
  const storageWarning = storageStatus === 'warning';
  const storageDangerNow = storageStatusNow === 'danger';
  const storageWarningNow = storageStatusNow === 'warning' || storageStatusNow === 'danger';
  const storageConfigured = storage?.configured === true;
  const storageUnknown = storageStatusNow === 'unknown' || storageStatus === 'unknown';
  const storagePulseColor =
    storageDanger
      ? '#dc3545'
      : storageWarning
      ? '#fd7e14'
      : !storageConfigured || storageUnknown
      ? '#dc3545'
      : '#198754';
  const storageBorderColor = storageDanger ? '#dc3545' : storageWarning ? '#fd7e14' : undefined;
  const storageMounts = Array.isArray(storage?.mounts) ? storage.mounts : [];
  const storageWorstMount =
    storageMounts.length > 0
      ? [...storageMounts].sort((a, b) => Number(b?.usedPct || 0) - Number(a?.usedPct || 0))[0]
      : null;
  const storageMountLabel = storageWorstMount
    ? storageWorstMount.path === storageWorstMount.mount
      ? storageWorstMount.mount
      : `${storageWorstMount.path} (${storageWorstMount.mount})`
    : '—';
  const storageVolumeDisplay =
    storage?.volumeType && Number.isFinite(Number(storage?.volumeSizeGiB))
      ? `${String(storage.volumeType).toUpperCase()} (${Number(storage.volumeSizeGiB).toFixed(0)} GiB)`
      : storage?.volumeType
      ? String(storage.volumeType).toUpperCase()
      : Number.isFinite(Number(storage?.volumeSizeGiB))
      ? `${Number(storage.volumeSizeGiB).toFixed(0)} GiB`
      : '—';
  const storageSizeDisplay = storageWorstMount
    ? formatBytes(storageWorstMount?.sizeBytes)
    : Number.isFinite(Number(storage?.volumeSizeGiB))
    ? `${Number(storage?.volumeSizeGiB).toFixed(0)} GiB`
    : '—';
  const storageUsedDisplay = storageWorstMount
    ? `${formatBytes(storageWorstMount?.usedBytes)} (${formatPct(storageWorstMount?.usedPct)})`
    : Number.isFinite(Number(storage?.worstUsedPct))
    ? formatPct(storage?.worstUsedPct)
    : '—';
  const storageFreeDisplay = storageWorstMount ? formatBytes(storageWorstMount?.availBytes) : '—';
  const inboundQueueOk = payload?.inbound?.queue?.ok;
  const inboundStatusLabel =
    payload?.inbound?.configured === false
      ? 'Not configured'
      : inboundQueueOk === true
      ? 'OK'
      : inboundQueueOk === false
      ? 'Down'
      : 'Unknown';
  const inboundPulseColor =
    inboundQueueOk === true
      ? '#198754'
      : inboundQueueOk === false
      ? '#dc3545'
      : '#6c757d';
  const inboundBadgeVariant =
    inboundStatusLabel === 'OK'
      ? 'success'
      : inboundStatusLabel === 'Down'
      ? 'danger'
      : inboundStatusLabel === 'Not configured'
      ? 'secondary'
      : 'warning';
  const isCapacitySustainedHigh = imapCapacity?.sustainedHigh === true;
  const isCapacityHighNow = imapCapacity?.highNow === true;
  const imapPulseColor =
    payload?.imap?.banner?.ok === false ? '#dc3545' : isCapacitySustainedHigh ? '#fd7e14' : '#198754';
  const imapBorderColor =
    payload?.imap?.banner?.ok === false ? '#dc3545' : isCapacitySustainedHigh ? '#fd7e14' : undefined;
  const [showImapHelp, setShowImapHelp] = React.useState(false);
  const [showImapCaps, setShowImapCaps] = React.useState(false);
  const [showDnsInstructions, setShowDnsInstructions] = React.useState(false);
  const [showStorageHelp, setShowStorageHelp] = React.useState(false);
  const tileMinHeight = compactCards ? 120 : 260;
  const shouldShowCard = (...groups: StatusCardFilter[]) => cardFilter === 'all' || groups.includes(cardFilter);

  return (
    <>
      <EMMSectionHeader title="Email Server Status" showHelp />
      <div className={`pt-3 ${compactCards ? 'kg-status-compact' : ''}`} style={{ maxWidth: 1200 }}>
      {/* Action bar - same style as other pages */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={() => refetch()}
          disabled={isLoading || isFetching}
          title="Refresh email server status"
        >
          <ArrowClockwise
            size={14}
            className="me-1"
            style={(isLoading || isFetching) ? { animation: 'spin 1s linear infinite' } : undefined}
          />
          {(isLoading || isFetching) ? 'Refreshing...' : 'Refresh'}
        </Button>
        <Form.Check
          type="switch"
          id="auto-refresh-email-server-status"
          label="Auto Refresh"
          checked={autoRefresh}
          onChange={(e) => setAutoRefresh(e.target.checked)}
          className="mb-0"
          style={{ fontSize: '0.875rem' }}
        />
        <div className="ms-auto d-flex align-items-center gap-1">
          <span className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
            Card Size
          </span>
          <Button
            variant={compactCards ? 'secondary' : 'outline-secondary'}
            size="sm"
            onClick={() => setCompactCards(true)}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
          >
            Compact
          </Button>
          <Button
            variant={!compactCards ? 'secondary' : 'outline-secondary'}
            size="sm"
            onClick={() => setCompactCards(false)}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
          >
            Current Size
          </Button>
        </div>
      </div>
      <div className="d-flex align-items-center gap-1 mb-3 flex-wrap">
        <span className="text-muted me-1" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
          Filter
        </span>
        {STATUS_CARD_FILTERS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={cardFilter === opt.id ? 'secondary' : 'outline-secondary'}
            onClick={() => setCardFilter(opt.id)}
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
          >
            {opt.label}
          </Button>
        ))}
      </div>
      <style>{`
        /* Make the tile header readable on narrower screens:
           keep big server image on desktop, but shrink it + tighten padding when needed. */
        .kg-tile-icon { width: 112px; height: 112px; }
        @media (max-width: 1200px) {
          .kg-tile-icon { width: 84px !important; height: 84px !important; }
        }
        @media (max-width: 992px) {
          .kg-tile-icon { width: 72px !important; height: 72px !important; }
        }

        @keyframes kgMonitorPulse {
          0% { transform: scale(1); opacity: 0.75; box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.35); }
          70% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 10px rgba(13, 110, 253, 0.0); }
          100% { transform: scale(1); opacity: 0.8; box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.0); }
        }
        .kg-monitor-pulse {
          animation: kgMonitorPulse 2.4s ease-out infinite;
          will-change: box-shadow, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .kg-monitor-pulse { animation: none !important; }
        }

        /* Status tiles: consistent sizing + cleaner look */
        .kg-status-tile {
          border-radius: 10px;
          overflow: hidden;
        }

        .kg-status-compact .kg-status-tile .kg-tile-header {
          padding: 10px 10px !important;
        }
        .kg-status-compact .kg-status-tile .kg-tile-icon {
          width: 62px !important;
          height: 62px !important;
        }
        .kg-status-compact .kg-status-tile .kg-tile-title {
          font-size: 0.95rem;
          line-height: 1.05;
        }
        .kg-status-compact .kg-status-tile .kg-tile-subtitle {
          font-size: 0.78rem !important;
        }
        .kg-status-compact .kg-status-tile .card-body {
          display: none !important;
        }
        .kg-status-compact .kg-status-tile .kg-kv-stack {
          gap: 4px !important;
          margin-top: 6px !important;
        }
        .kg-status-compact .kg-status-tile .kg-kv-row {
          font-size: 0.76rem !important;
          line-height: 1.08 !important;
        }

        /* IMAP capability tiles */
        .kg-cap-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 10px;
        }
        @media (min-width: 576px) {
          .kg-cap-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (min-width: 992px) {
          .kg-cap-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        .kg-cap-tile {
          border: 1px solid #e9ecef;
          background: #fff;
          border-radius: 10px;
          padding: 10px 10px;
          min-height: 92px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .kg-cap-title {
          font-weight: 700;
          font-size: 0.85rem;
          line-height: 1.15;
        }
        .kg-cap-desc {
          color: #6c757d;
          font-size: 0.8rem;
          line-height: 1.2;
        }
      `}</style>

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            Read-only health checks for SMTP / IMAP / inbound and outbound path monitoring (all relays/providers). DNS auth is on the Domains page.
          </div>
        </div>
      </div>

      {error && (
        <Card className="mb-3 border-danger">
          <Card.Body>
            <div className="text-danger fw-bold">Failed to load platform status</div>
            <div style={{ fontSize: '0.85rem' }}>{String((error as any)?.error || (error as any)?.message || error)}</div>
          </Card.Body>
        </Card>
      )}

      {isCapacitySustainedHigh && (
        <Card className="mb-3 border-warning">
          <Card.Body>
            <div className="fw-bold text-warning mb-1">IMAP login saturation alert (sustained)</div>
            <div style={{ fontSize: '0.9rem' }}>
              IMAP login utilization is high for at least{' '}
              <span className="fw-bold">{formatDurationMinutes(imapCapacity?.sustainedSeconds)}</span> at threshold{' '}
              <span className="fw-bold">{formatPct(imapCapacity?.thresholdPct)}</span>. Check this page for details.
            </div>
          </Card.Body>
        </Card>
      )}

      {!isCapacitySustainedHigh && isCapacityHighNow && (
        <Card className="mb-3 border-warning">
          <Card.Body>
            <div className="fw-bold text-warning mb-1">IMAP login utilization elevated</div>
            <div style={{ fontSize: '0.9rem' }}>
              Current usage is above threshold but not sustained long enough to trigger the sustained alert window yet.
            </div>
          </Card.Body>
        </Card>
      )}

      {storageDanger && (
        <Card className="mb-3 border-danger">
          <Card.Body>
            <div className="fw-bold text-danger mb-1">Mailbox storage danger (sustained)</div>
            <div style={{ fontSize: '0.9rem' }}>
              Storage is above <span className="fw-bold">{formatPct(storage?.dangerThresholdPct)}</span> for at least{' '}
              <span className="fw-bold">{formatDurationMinutes(storage?.sustainedSeconds)}</span>.
            </div>
          </Card.Body>
        </Card>
      )}

      {!storageDanger && storageWarning && (
        <Card className="mb-3 border-warning">
          <Card.Body>
            <div className="fw-bold text-warning mb-1">Mailbox storage saturated (sustained)</div>
            <div style={{ fontSize: '0.9rem' }}>
              Storage is above <span className="fw-bold">{formatPct(storage?.warnThresholdPct)}</span> for at least{' '}
              <span className="fw-bold">{formatDurationMinutes(storage?.sustainedSeconds)}</span>.
            </div>
          </Card.Body>
        </Card>
      )}

      <Row className="g-3 align-items-stretch">
        <Col md={6} lg={4} xl={3} style={!shouldShowCard('inbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="Postmark Inbound"
              subtitle="(Provider)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color={inboundPulseColor} />
                  <StatusBadge ok={inboundQueueOk} />
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={inboundStatusLabel} />
                <KV label="Configured" value={payload?.inbound?.configured ? 'Yes' : 'No'} />
                <KV label="Provider" value="Postmark" />
                <KV label="Queue" value="Depth" />
                <KV label="Visible" value={payload?.inbound?.queue?.approximateVisible ?? '—'} />
                <KV label="In-flight" value={payload?.inbound?.queue?.approximateInFlight ?? '—'} />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Postmark → KG ingestion queue
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('inbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="Inbound Path"
              subtitle="(All relays)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color={inboundPulseColor} />
                  <Badge
                    bg={inboundBadgeVariant}
                    text={inboundBadgeVariant === 'warning' ? 'dark' : undefined}
                    className="text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem' }}
                    title="Shared inbound ingestion path"
                  >
                    {inboundStatusLabel}
                  </Badge>
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={inboundStatusLabel} />
                <KV label="Scope" value="All inbound relays/providers" />
                <KV label="Coverage" value="Shared inbound ingestion dependencies" />
                <KV label="Signal" value="Inbound queue health" />
                <KV label="Rule" value="Queue reachable + healthy" />
                <KV label="Last update" value={payload?.now || '—'} />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Monitors inbound path used by Postmark and SES receiving domains
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('inbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="SES Inbound"
              subtitle="(Provider)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color={inboundPulseColor} />
                  <StatusBadge ok={inboundQueueOk} />
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={inboundStatusLabel} />
                <KV label="Configured" value={payload?.inbound?.configured ? 'Yes' : 'No'} />
                <KV label="Provider" value="SES" />
                <KV label="Scope" value="Domains set to SES" />
                <KV label="MX target" value="inbound-smtp.us-east-1.amazonaws.com" />
                <KV label="Signal" value="Unified inbound queue" />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                SES receiving domains feed the shared KG inbound ingestion path
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('outbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="SMTP Submission"
              subtitle="(kg-smtp)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color={payload?.smtpSubmission?.ok ? '#198754' : '#dc3545'} />
                  <StatusBadge ok={payload?.smtpSubmission?.ok} />
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV
                  label="Status"
                  value={
                    payload?.smtpSubmission?.configured === false
                      ? 'Not configured'
                      : payload?.smtpSubmission?.ok
                      ? 'OK'
                      : payload?.smtpSubmission?.ok === false
                      ? 'Down'
                      : 'Unknown'
                  }
                />
                <KV label="Configured" value={payload?.smtpSubmission?.configured ? 'Yes' : 'No'} />
                <KV label="Server" value="kg-smtp" />
                <KV label="Host" value={payload?.smtpSubmission?.host || '—'} />
                <KV
                  label="Ports"
                  value={`${payload?.smtpSubmission?.ports?.submission ?? 587} / ${payload?.smtpSubmission?.ports?.smtps ?? 465}`}
                />
                <KV
                  label="TCP (587)"
                  value={
                    payload?.smtpSubmission?.tcp587?.ok
                      ? `OK (${payload?.smtpSubmission?.tcp587?.latencyMs ?? '-'}ms)`
                      : payload?.smtpSubmission?.tcp587?.ok === false
                      ? `${payload?.smtpSubmission?.tcp587?.detail || 'Down'}`
                      : '—'
                  }
                />
                <KV
                  label="TCP (465)"
                  value={
                    payload?.smtpSubmission?.tcp465?.ok
                      ? `OK (${payload?.smtpSubmission?.tcp465?.latencyMs ?? '-'}ms)`
                      : payload?.smtpSubmission?.tcp465?.ok === false
                      ? `${payload?.smtpSubmission?.tcp465?.detail || 'Down'}`
                      : '—'
                  }
                />
                <KV
                  label="STARTTLS"
                  value={
                    payload?.smtpSubmission?.starttls?.advertised
                      ? 'Advertised'
                      : payload?.smtpSubmission?.starttls?.advertised === false
                      ? 'Not advertised'
                      : '—'
                  }
                />
                <KV
                  label="Auth"
                  value={
                    payload?.smtpSubmission?.configured === false
                      ? 'Not configured'
                      : payload?.smtpSubmission?.auth?.ok === true
                      ? 'OK'
                      : payload?.smtpSubmission?.auth?.ok === false
                      ? (payload?.smtpSubmission?.auth?.detail || 'Failed')
                      : 'Not configured'
                  }
                />
                <KV
                  label="NLB"
                  value={
                    payload?.smtpSubmission?.configured === false
                      ? 'Not configured'
                      : payload?.smtpSubmission?.nlb?.ok
                      ? 'Healthy'
                      : payload?.smtpSubmission?.nlb?.ok === false
                      ? 'Unhealthy'
                      : 'Not configured'
                  }
                />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Accept mail from users
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('outbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="SMTP Outbound"
              subtitle="(SMTP.com)"
              right={
                <div className="d-flex align-items-center">
                  <Badge
                    bg="light"
                    text="dark"
                    className="me-2 text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem', border: '1px solid #dee2e6' }}
                    title="Third-party provider (final delivery)"
                  >
                    Provider
                  </Badge>
                  <MonitoringPulse color={payload?.smtp?.tcp?.ok ? '#198754' : '#dc3545'} />
                  <StatusBadge ok={payload?.smtp?.tcp?.ok} />
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={payload?.smtp?.tcp?.ok ? 'OK' : payload?.smtp?.tcp?.ok === false ? 'Down' : 'Unknown'} />
                <KV label="Configured" value={payload?.smtp?.configured ? 'Yes' : 'No'} />
                <KV label="Server" value="SMTP.com" />
                <KV label="Host" value={payload?.smtp?.host || '—'} />
                <KV label="Port" value={payload?.smtp?.port ?? '—'} />
                <KV
                  label="TCP"
                  value={
                    payload?.smtp?.tcp?.ok
                      ? `OK (${payload?.smtp?.tcp?.latencyMs ?? '-'}ms)`
                      : payload?.smtp?.tcp?.ok === false
                      ? `${payload?.smtp?.tcp?.detail || 'Down'}`
                      : '—'
                  }
                />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Deliver mail to the internet
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('outbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="Mailgun Outbound"
              subtitle="(Relay)"
              right={
                <div className="d-flex align-items-center">
                  <Badge
                    bg="light"
                    text="dark"
                    className="me-2 text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem', border: '1px solid #dee2e6' }}
                    title="Third-party provider (final delivery)"
                  >
                    Provider
                  </Badge>
                  <MonitoringPulse color={outboundCanaryPulseColor} />
                  <Badge
                    bg={outboundCanaryBadgeVariant}
                    text={outboundCanaryBadgeVariant === 'warning' ? 'dark' : undefined}
                    className="text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem' }}
                    title="Status sourced from shared outbound-path canary"
                  >
                    {outboundCanaryStatusLabel}
                  </Badge>
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={outboundCanaryStatusLabel} />
                <KV label="Configured" value="Per domain / pool" />
                <KV label="Provider" value="Mailgun" />
                <KV label="Scope" value="Outbound relay" />
                <KV label="Signal" value="Shared outbound canary" />
                <KV label="Relay probe" value="Pending" />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Used for Mailgun-routed domains; provider health currently follows shared outbound path status
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('outbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="MailChannels Outbound"
              subtitle="(Relay)"
              right={
                <div className="d-flex align-items-center">
                  <Badge
                    bg="light"
                    text="dark"
                    className="me-2 text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem', border: '1px solid #dee2e6' }}
                    title="Third-party provider (final delivery)"
                  >
                    Provider
                  </Badge>
                  <MonitoringPulse color={outboundCanaryPulseColor} />
                  <Badge
                    bg={outboundCanaryBadgeVariant}
                    text={outboundCanaryBadgeVariant === 'warning' ? 'dark' : undefined}
                    className="text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem' }}
                    title="Status sourced from shared outbound-path canary"
                  >
                    {outboundCanaryStatusLabel}
                  </Badge>
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={outboundCanaryStatusLabel} />
                <KV label="Configured" value="Per domain / pool" />
                <KV label="Provider" value="MailChannels" />
                <KV label="Scope" value="Outbound relay" />
                <KV label="Signal" value="Shared outbound canary" />
                <KV label="Relay probe" value="Pending" />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Used for MailChannels-routed domains; provider health currently follows shared outbound path status
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('outbound') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="Outbound Path"
              subtitle="(All relays)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color={outboundCanaryPulseColor} />
                  <Badge
                    bg={outboundCanaryBadgeVariant}
                    text={outboundCanaryBadgeVariant === 'warning' ? 'dark' : undefined}
                    className="text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem' }}
                    title="Shared outbound send-path canary (no-send)"
                  >
                    {outboundCanaryStatusLabel}
                  </Badge>
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={outboundCanaryStatusLabel} />
                <KV label="Scope" value="All outbound relays" />
                <KV label="Coverage" value="Shared send-path dependencies" />
                <KV label="Signal" value="Outbound canary alarm" />
                <KV label="Rule" value={outboundCanaryConfigured ? '2 failures / 30m' : '—'} />
                <KV label="Last update" value={outboundCanary?.stateUpdatedAt || '—'} />
              </KVStack>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Monitors outbound send path used by SMTP.com, Mailgun, MailChannels, SendGrid, and other relays
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('imap') ? { display: 'none' } : undefined}>
          <Card
            className="h-100 kg-status-tile"
            style={{ minHeight: tileMinHeight, borderColor: imapBorderColor }}
          >
            <TileHeader
              title="IMAP"
              subtitle="(Mailbox)"
              right={
                <div className="d-flex align-items-center">
                  {isCapacitySustainedHigh && (
                    <Badge
                      bg="warning"
                      text="dark"
                      className="me-2 text-nowrap"
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem' }}
                    >
                      High load
                    </Badge>
                  )}
                  <MonitoringPulse color={imapPulseColor} />
                  <StatusBadge ok={payload?.imap?.banner?.ok} />
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value={payload?.imap?.banner?.ok ? 'OK' : payload?.imap?.banner?.ok === false ? 'Down' : 'Unknown'} />
                <KV label="Configured" value={payload?.imap?.configured ? 'Yes' : 'No'} />
                <KV label="Server" value="IMAP" />
                <KV label="Host" value={payload?.imap?.host || '—'} />
                <KV label="Port" value={payload?.imap?.port ?? '—'} />
                <KV
                  label="Banner"
                  value={
                    payload?.imap?.banner?.ok
                      ? `OK (${payload?.imap?.banner?.latencyMs ?? '-'}ms)`
                      : payload?.imap?.banner?.ok === false
                      ? `${payload?.imap?.banner?.detail || 'Down'}`
                      : '—'
                  }
                />
                <KV
                  label="Login util"
                  value={
                    imapCapacity?.loginLimit
                      ? `${formatPct(imapCapacity?.loginUtilizationPct)} of ${imapCapacity?.loginLimit}`
                      : '—'
                  }
                />
                <KV
                  label="IMAP util"
                  value={
                    imapCapacity?.imapLimit
                      ? `${formatPct(imapCapacity?.imapUtilizationPct)} of ${imapCapacity?.imapLimit}`
                      : '—'
                  }
                />
                <KV
                  label="Saturation"
                  value={
                    isCapacitySustainedHigh
                      ? 'High (sustained)'
                      : isCapacityHighNow
                      ? 'Elevated'
                      : 'Normal'
                  }
                />
                <KV
                  label="Alert rule"
                  value={`${formatPct(imapCapacity?.thresholdPct)} for ${formatDurationMinutes(
                    imapCapacity?.sustainedSeconds
                  )}`}
                />
                <KV
                  label="Auth drift probe"
                  value={<span title={authCanary.detail}>{authCanary.label}</span>}
                />
                <KV label="Auth probe last run" value={payload?.imap?.authCanary?.lastSampledAt || '—'} />
                <KV
                  label="Auth probe remediation"
                  value={payload?.imap?.authCanary?.lastRemediationStatus || '—'}
                />
                <KV label="Last drift event" value={payload?.imap?.authCanary?.lastFailureAt || '—'} />
                <KV label="Last drift domain" value={payload?.imap?.authCanary?.lastFailureDomain || '—'} />
                <KV label="Repair command" value={payload?.imap?.authCanary?.lastRepairCommandId || '—'} />
              </KVStack>

              {payload?.imap?.banner?.ok ? (
                <>
                  {imapCaps.length > 0 && (
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setShowImapCaps((v) => !v)}
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                      >
                        {showImapCaps ? 'Hide' : 'Show'} Capabilities ({imapCaps.length})
                      </Button>
                      <Collapse in={showImapCaps}>
                        <div>
                          <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                            Capabilities (explained)
                          </div>
                          <div className="mt-2 kg-cap-grid">
                            {imapCaps.map((c) => (
                              <CapabilityTile key={c} cap={c} />
                            ))}
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  )}
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setShowImapHelp(true)}
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                    >
                      What does this mean?
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-1 text-danger" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    IMAP is DOWN — Instantly + iOS Mail will not work
                  </div>
                </>
              )}
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Mailbox access (993/TLS)
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('storage', 'imap') ? { display: 'none' } : undefined}>
          <Card
            id="storage-status"
            className="h-100 kg-status-tile"
            style={{ minHeight: tileMinHeight, borderColor: storageBorderColor, cursor: 'pointer' }}
            onClick={() => {
              window.location.hash = 'storage-status';
            }}
          >
            <TileHeader
              title="IMAP Storage"
              subtitle="(Mailbox)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color={storagePulseColor} />
                  <StorageStatusBadge status={storageStatus} />
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV
                  label="Status"
                  value={
                    storage?.configured === false
                      ? 'Not configured'
                      : storageDanger
                      ? 'Danger'
                      : storageWarning
                      ? 'Warning'
                      : storageDangerNow
                      ? 'Danger (observing)'
                      : storageWarningNow
                      ? 'Warning (observing)'
                      : storageStatusNow === 'unknown'
                      ? 'Unknown'
                      : 'OK'
                  }
                />
                <KV label="Configured" value={storage?.configured ? 'Yes' : 'No'} />
                <KV label="Server" value="Mailbox" />
                <KV label="Volume" value={storageVolumeDisplay} />
                <KV label="Size" value={storageSizeDisplay} />
                <KV label="Used" value={storageUsedDisplay} />
                <KV label="Free" value={storageFreeDisplay} />
                <KV label="Mount" value={storageMountLabel} />
                <KV
                  label="Inode util"
                  value={
                    Number.isFinite(Number(storageWorstMount?.inodeUsedPct))
                      ? formatPct(storageWorstMount?.inodeUsedPct)
                      : Number.isFinite(Number(storage?.worstInodeUsedPct))
                      ? formatPct(storage?.worstInodeUsedPct)
                      : '—'
                  }
                />
                <KV
                  label="Alert rule"
                  value={`${formatPct(storage?.warnThresholdPct)} / ${formatPct(
                    storage?.dangerThresholdPct
                  )} for ${formatDurationMinutes(storage?.sustainedSeconds)}`}
                />
                <KV
                  label="Saturation"
                  value={
                    storageDangerNow
                      ? 'Danger now'
                      : storageWarningNow
                      ? 'Elevated now'
                      : storageStatusNow === 'unknown'
                      ? 'Unknown'
                      : 'Normal'
                  }
                />
              </KVStack>
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStorageHelp((v) => !v);
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                >
                  How to add storage?
                </Button>
                <Collapse in={showStorageHelp}>
                  <div className="mt-2 text-muted" style={{ fontSize: '0.82rem' }}>
                    Increase the EBS volume for the IMAP instance, then run filesystem expansion on-host
                    (for example: growpart + resize2fs/xfs_growfs). Refresh this page to confirm.
                  </div>
                </Collapse>
              </div>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                Mailbox disk capacity on active IMAP host
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4} xl={3} style={!shouldShowCard('dns') ? { display: 'none' } : undefined}>
          <Card className="h-100 kg-status-tile" style={{ minHeight: tileMinHeight }}>
            <TileHeader
              title="DNS"
              subtitle="(Auth)"
              right={
                <div className="d-flex align-items-center">
                  <MonitoringPulse color="#0dcaf0" />
                  <Badge
                    bg="info"
                    className="text-nowrap"
                    style={{ fontSize: '0.72rem', padding: '0.25rem 0.45rem' }}
                  >
                    Domains
                  </Badge>
                </div>
              }
            />
            <Card.Body className="d-flex flex-column">
              <KVStack>
                <KV label="Status" value="Managed" />
                <KV label="Configured" value={payload?.dns?.configured === false ? 'No' : 'Yes'} />
                <KV label="Server" value="DNS" />
                <KV label="Auth" value="SPF / DKIM / DMARC" />
                <KV label="Where" value="Domains page" />
              </KVStack>
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowDnsInstructions((v) => !v)}
                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                >
                  {showDnsInstructions ? 'Hide' : 'Show'} Instructions
                </Button>
                <Collapse in={showDnsInstructions}>
                  <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                    Use Domains → Auth modal to verify/update records.
                  </div>
                </Collapse>
              </div>
              <div className="mt-auto pt-2 text-muted" style={{ fontSize: '0.8rem' }}>
                DNS authentication status per domain
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showImapHelp} onHide={() => setShowImapHelp(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>IMAP Banner — What it means</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="kg-cap-grid mb-3">
            <div className="kg-cap-tile">
              <div className="kg-cap-title">OK</div>
              <div className="kg-cap-desc">The server is saying “everything is ok” and it’s ready.</div>
            </div>
            <div className="kg-cap-tile">
              <div className="kg-cap-title">[CAPABILITY …]</div>
              <div className="kg-cap-desc">
                This is a list of features the IMAP server supports so mail clients know what they can use.
              </div>
            </div>
          </div>

          {imapBannerInfo?.capabilities?.length ? (
            <div className="kg-cap-grid mb-3">
              {imapCaps.map((cap) => (
                <CapabilityTile key={cap} cap={cap} />
              ))}
            </div>
          ) : (
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
              No capability list found in the banner.
            </div>
          )}

          {imapBannerInfo?.serverText && (
            <Card>
              <Card.Body className="py-2">
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>Raw banner</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>
                  {imapBannerInfo.serverText}
                </div>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowImapHelp(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Card className="mt-3">
        <Card.Body>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            Stage: <span className="fw-bold">{payload?.stage || '—'}</span> · Last checked:{' '}
            <span className="fw-bold">{payload?.now || '—'}</span> · Capacity sample:{' '}
            <span className="fw-bold">{imapCapacity?.sampledAt || '—'}</span> · Storage sample:{' '}
            <span className="fw-bold">{storage?.sampledAt || '—'}</span>
          </div>
        </Card.Body>
      </Card>
      </div>
    </>
  );
}
